import React, { useState, useEffect } from "react";
import { Info } from "lucide-react";
import { decryptData } from "../../../utils/storageCrypto";

const categories = [
  { key: "quality", label: "Quality", tooltip: "Measures the overall standard of products or services delivered by the supplier." },
  { key: "communication", label: "Communication", tooltip: "Evaluates how clearly and promptly the supplier communicates updates and issues." },
  { key: "compliance", label: "Compliance", tooltip: "Indicates how well the supplier adheres to policies, regulations, and contract terms." },
  { key: "reliability", label: "Reliability", tooltip: "Represents consistency in fulfilling commitments like delivery and service." },
  { key: "service_support", label: "Service Support", tooltip: "Assesses post-sale support, issue resolution, and responsiveness." }
];

const Star = ({ filled, onClick }) => (
  <span
    onClick={onClick}
    className={`cursor-pointer text-xl ${
      filled ? "text-yellow-400" : "text-gray-300 hover:text-yellow-300"
    }`}
  >
    ★
  </span>
);

const Tooltip = ({ text }) => (
  <div className="relative group ml-2">
    <Info className="w-4 h-4 text-gray-400 cursor-pointer" />
    <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 w-48 z-10">
      {text}
    </div>
  </div>
);

export default function SupplierRating({ supplierId, quotationId}) {
  const [ratings, setRatings] = useState({
    quality: 0,
    communication: 0,
    compliance: 0,
    reliability: 0,
    service_support: 0
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ratingId, setRatingId] = useState(null);

  // 🔹 Fetch existing rating
  useEffect(() => {
    if (!supplierId) return;

const fetchRating = async () => {
  try {
    setLoading(true);

    const token = decryptData(sessionStorage.getItem("token"));

    const res = await fetch(
      `${process.env.REACT_APP_FORE_BRICKBUCKET}customer/supplier-ratings/get-admin-rating?vendor_id=${supplierId}&quotation_id=${quotationId}`,
      {
        headers: {
          "Content-Type": "application/json",
          "x-token": token,
        },
      }
    );

    const data = await res.json();
    console.log("Fetched rating:", data);

    if (data?.data) {
      setRatings({
        quality: data.data.quality || 0,
        communication: data.data.communication || 0,
        compliance: data.data.compliance || 0,
        reliability: data.data.reliability || 0,
        service_support: data.data.service_support || 0,
      });
    }

  } catch (err) {
    console.error("Error fetching rating:", err);
  } finally {
    setLoading(false);
  }
};

    fetchRating();
  }, [supplierId]);

  const handleRating = (key, value) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
  };

const weights = {
  quality: 0.2,
  communication: 0.2,
  compliance: 0.2,
  reliability: 0.2,
  service_support: 0.2
};

const calculateOverall = () => {
  return Object.keys(ratings).reduce(
    (sum, key) => sum + ratings[key] * weights[key],
    0
  ).toFixed(2);
};

  // 🔹 Submit rating
const handleSubmit = async () => {
  try {
    setSaving(true);

    const token = decryptData(sessionStorage.getItem("token"));

    const payload = {
      quotation_id: quotationId,
      order_id: null,
      vendor_id: supplierId,

      admin_rating: {
        quality: ratings.quality,
        communication: ratings.communication,
        compliance: ratings.compliance,
        reliability: ratings.reliability,
        service_support: ratings.service_support
      }
    };

    const res = await fetch(
      `${process.env.REACT_APP_FORE_BRICKBUCKET}customer/supplier-ratings/create-or-update-supplier-rating`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-token": token,
        },
        body: JSON.stringify(payload),
      }
    );

    const data = await res.json();

    console.log("Rating submission response:", data);

    if (data?.success) {
      alert("Rating saved successfully ✅");
    } else {
      throw new Error("Failed");
    }

  } catch (err) {
    console.error("Error saving rating:", err);
    alert("Failed to save rating ❌");
  } finally {
    setSaving(false);
  }
};

  if (loading) {
    return <div className="text-sm text-gray-500">Loading rating...</div>;
  }

  return (
    <div className="p-4 bg-white border rounded-xl shadow-sm w-[320px]">
{/*       <h3 className="text-sm font-semibold mb-3 text-gray-800">
        Admin Rating
      </h3> */}

      <div className="space-y-3">
        {categories.map((cat) => (
          <div key={cat.key} className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-xs text-gray-700">
                {cat.label}
              </span>
              <Tooltip text={cat.tooltip} />
            </div>

            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  filled={ratings[cat.key] >= star}
                  onClick={() => handleRating(cat.key, star)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Overall */}
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-500">Overall</p>
        <p className="text-lg font-bold text-yellow-500">
          {calculateOverall()} / 5
        </p>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={saving}
        className="mt-4 w-full bg-blue-600 text-white py-1.5 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? "Saving..." : ratingId ? "Update Rating" : "Submit Rating"}
      </button>
    </div>
  );
}